import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Spinner, Alert } from 'react-bootstrap';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../../api/apiClient';
import { generateModulesUrl } from '../../../utils/urlSecurity';

const EnrolledCoursesPage = () => {
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { user } = useAuth();
  const navigate = useNavigate();

  const defaultCourseImage = 'https://via.placeholder.com/300x200/17a2b8/ffffff?text=Course+Image';
  const formatScheduleDateTime = (value) => {
    if (!value) {
      return null;
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }

    return parsed.toLocaleString();
  };

  const extractSkillName = (skill) => {
    if (!skill) {
      return null;
    }

    if (typeof skill === 'string') {
      return skill;
    }

    if (typeof skill === 'object') {
      const keyCandidates = ['name', 'skill_name', 'skillName', 'title', 'label'];
      const directMatch = keyCandidates
        .map((key) => (typeof skill[key] === 'string' ? skill[key] : null))
        .find((value) => value && value.trim().length > 0);

      if (directMatch) {
        return directMatch;
      }

      if (skill.Skill) {
        return extractSkillName(skill.Skill);
      }

      if (skill.skill) {
        return extractSkillName(skill.skill);
      }

      const firstStringValue = Object.values(skill).find(
        (value) => typeof value === 'string' && value.trim().length > 0
      );

      if (firstStringValue) {
        return firstStringValue;
      }
    }

    return null;
  };

  const deriveCourseSkills = (course) => {
    if (!course) {
      return [];
    }

    const skillSources = [
      course.skills,
      course.CourseSkills,
      course.skills_accrued,
      course.skillsAccrued,
      course.skill_tags
    ];

    return skillSources
      .flatMap((collection) => (Array.isArray(collection) ? collection : []))
      .map(extractSkillName)
      .filter((skill, index, array) => Boolean(skill) && array.indexOf(skill) === index);
  };

  const resolveCoursePrice = (course) => {
    if (!course) {
      return null;
    }

    const priceCandidates = [
      course.price,
      course.course_price,
      course.coursePrice,
      course.cost,
      course.amount,
      course.CoursePrice?.price,
      course.CoursePricing?.price,
      course.pricing?.price,
      course.pricing?.amount,
      course.Pricing?.price,
      course.Pricing?.amount
    ];

    return priceCandidates.find((value) => value !== undefined && value !== null);
  };

  const getCoursePriceLabel = (course) => {
    const priceValue = resolveCoursePrice(course);

    if (priceValue === undefined || priceValue === null) {
      return 'Free';
    }

    if (typeof priceValue === 'string' && priceValue.trim().length === 0) {
      return 'Free';
    }

    const numericPrice = Number(priceValue);

    if (Number.isNaN(numericPrice)) {
      return typeof priceValue === 'string' ? priceValue : 'Paid';
    }

    if (numericPrice === 0) {
      return 'Free';
    }

    return `₹${numericPrice.toFixed(2)}`;
  };

  const fetchEnrolledCourses = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.enrollments.getByUser(user.id);

      if (response.data.success) {
        setEnrolledCourses(response.data.data || []);
      } else {
        setEnrolledCourses(response.data || []);
      }
    } catch (fetchError) {
      if (fetchError.response?.status === 403) {
        setError('Your account is inactive or has been deleted. Please contact support.');
        return;
      }

      setError(fetchError.response?.data?.message || 'Failed to load enrolled courses');
    } finally {
      setLoading(false);
    }
  };

  const handleViewCourse = (courseId) => {
    const modulesUrl = generateModulesUrl(courseId, 'user');
    navigate(modulesUrl);
  };

  useEffect(() => {
    if (!user?.id) {
      return;
    }

    fetchEnrolledCourses();
  }, [user?.id]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 p-4 sm:p-6 md:p-8 lg:p-10">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl px-5 py-4 sm:p-6 border border-purple-100 shadow-xl">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl sm:rounded-2xl flex items-center justify-center text-white text-xl sm:text-2xl shadow-md">
                <i className="fas fa-graduation-cap"></i>
              </div>
              <div>
                <h2 className="text-left text-2xl sm:text-2xl font-bold text-gray-900 mb-1">My Courses</h2>
                <p className="text-gray-600 text-sm sm:text-base m-0">
                  Continue your learning journey
                </p>
              </div>
            </div>
            {enrolledCourses.length > 0 && (
              <Badge bsPrefix="custom-hamburger" className="text-white bg-gradient-to-br from-purple-600 via-pink-600 to-red-600  font-semibold rounded-lg sm:rounded-xl  transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2 px-3">
                {enrolledCourses.length} Enrolled
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl border border-purple-100 shadow-xl p-4 sm:p-6">
        {loading ? (
          <div className="text-center py-12 sm:py-16">
            <div className="inline-block animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-4 border-purple-600 border-t-transparent mb-4"></div>
            <p className="text-gray-600 text-base sm:text-lg font-medium">Loading your enrolled courses...</p>
          </div>
        ) : error ? (
          <Alert variant="danger" className="rounded-xl border-2 border-red-200 bg-red-50">
            <div className="flex items-center gap-3">
              <i className="fas fa-exclamation-triangle text-red-600 text-xl"></i>
              <span className="font-semibold">{error}</span>
            </div>
          </Alert>
        ) : enrolledCourses.length === 0 ? (
          <div className="text-center py-12 sm:py-16">
            <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-purple-400 to-indigo-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <i className="fas fa-graduation-cap text-white text-3xl sm:text-3xl"></i>
            </div>
            <h5 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">No enrolled courses</h5>
            <p className="text-gray-600 text-base sm:text-lg">Browse available courses to start learning.</p>
          </div>
        ) : (
          <Row className="g-4">
            {enrolledCourses.map((enrollment) => {
              const course = enrollment.Course || enrollment;
              const courseTitle = course.title || course.name || 'Course';
              const enrollmentDate = enrollment.enrollment_date
                ? new Date(enrollment.enrollment_date).toLocaleDateString()
                : null;
              const scheduleStart = formatScheduleDateTime(enrollment.available_from);
              const scheduleEnd = formatScheduleDateTime(enrollment.available_to);
              const courseSkills = deriveCourseSkills(course);
              const priceLabel = getCoursePriceLabel(course);

              return (
                <Col lg={4} md={6} sm={12} key={enrollment.id || course.id}>
                  <Card className="h-100 border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                    <div style={{ height: '200px', overflow: 'hidden', backgroundColor: '#f8f9fa', position: 'relative' }}>
                      <Card.Img
                        variant="top"
                        src={course.course_image || defaultCourseImage}
                        alt={courseTitle}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          objectPosition: 'center',
                          transition: 'transform 0.3s ease'
                        }}
                        onMouseEnter={(event) => {
                          event.target.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(event) => {
                          event.target.style.transform = 'scale(1)';
                        }}
                      />
                      <Badge className="position-absolute top-2 end-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-3 py-1 rounded-full shadow-md font-semibold">
                        Enrolled
                      </Badge>
                    </div>
                    <Card.Body className="d-flex flex-column p-4">
                      <div className="flex-grow-1">
                        <h5 className="fw-bold text-gray-900 mb-2 text-lg">{courseTitle}</h5>
                        <p className="text-gray-600 mb-3 text-sm" style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          minHeight: '60px'
                        }}>
                          {course.description || 'No description available'}
                        </p>
                        <div className="mb-3 space-y-2">
                          {course.duration && (
                            <div className="d-flex align-items-center text-sm">
                              <i className="fas fa-clock text-purple-600 me-2"></i>
                              <span className="text-gray-700">{course.duration} mins</span>
                            </div>
                          )}
                          {course.category && (
                            <div className="d-flex align-items-center text-sm">
                              <i className="fas fa-tag text-purple-600 me-2"></i>
                              <span className="text-gray-700">{course.category}</span>
                            </div>
                          )}
                          {courseSkills.length > 0 && (
                            <div className="d-flex align-items-start text-sm">
                              <i className="fas fa-lightbulb text-purple-600 me-2 mt-1"></i>
                              <span className="text-gray-700">
                                {courseSkills.slice(0, 2).join(', ')}{courseSkills.length > 2 ? '...' : ''}
                              </span>
                            </div>
                          )}
                          {course.difficulty_level && (
                            <div className="d-flex align-items-center text-sm">
                              <i className="fas fa-star text-purple-600 me-2"></i>
                              <span className="text-gray-700">Level: {course.difficulty_level}</span>
                            </div>
                          )}
                        </div>
                        <div className="d-flex align-items-center mb-3 p-2 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg">
                          <i className="fas fa-rupee-sign text-purple-600 me-2"></i>
                          <span className="text-gray-900 font-semibold">{priceLabel}</span>
                        </div>
                        {enrollmentDate && (
                          <div className="d-flex align-items-center mb-3 text-sm">
                            <i className="fas fa-calendar-alt text-purple-600 me-2"></i>
                            <span className="text-gray-700">Enrolled: {enrollmentDate}</span>
                          </div>
                        )}
                        {(scheduleStart || scheduleEnd) && (
                          <div className="mb-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
                            <div className="d-flex align-items-center text-sm mb-2">
                              <i className="fas fa-calendar-check text-blue-600 me-2"></i>
                              <span className="text-gray-900 font-semibold">Course Schedule</span>
                            </div>
                            {scheduleStart && (
                              <div className="text-sm text-gray-700 mb-1">
                                Starts: {scheduleStart}
                              </div>
                            )}
                            {scheduleEnd && (
                              <div className="text-sm text-gray-700">
                                Ends: {scheduleEnd}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="mt-auto">
                        <Button
                          bsPrefix="custom-hamburger"
                          className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2 "
                         
                          onClick={() => handleViewCourse(course.id)}
                          aria-label={`View course ${courseTitle}`}
                        >
                          <i className="fas fa-eye me-2"></i>
                          View Course
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </div>
    </div>
  );
};

export default EnrolledCoursesPage;

